/**
 * Página do Serviço: Segurança & EPI
 * WorkStream - Detecção de EPIs e compliance com NRs
 */
import { Link } from 'react-router-dom'
import { 
  HardHat, Shield, Eye, AlertTriangle, FileCheck, Camera, Clock,
  ArrowRight, Play, CheckCircle2
} from 'lucide-react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

const epis = [
  { name: 'Capacete', icon: '🪖', detected: true },
  { name: 'Colete', icon: '🦺', detected: true },
  { name: 'Óculos', icon: '🥽', detected: true },
  { name: 'Luvas', icon: '🧤', detected: true },
  { name: 'Bota', icon: '👢', detected: true },
  { name: 'Protetor Auricular', icon: '🎧', detected: true },
  { name: 'Máscara', icon: '😷', detected: true },
  { name: 'Cinto', icon: '⛑️', detected: true },
]

const nrs = [
  { code: 'NR-6', name: 'Equipamentos de Proteção Individual', essential: true },
  { code: 'NR-10', name: 'Segurança em Instalações Elétricas', essential: true },
  { code: 'NR-12', name: 'Segurança no Trabalho em Máquinas', essential: false },
  { code: 'NR-18', name: 'Construção Civil', essential: true },
  { code: 'NR-32', name: 'Segurança na Saúde', essential: false },
  { code: 'NR-33', name: 'Espaços Confinados', essential: false },
  { code: 'NR-35', name: 'Trabalho em Altura', essential: true },
]

const features = [
  {
    icon: Eye,
    title: 'Detecção em Tempo Real',
    description: 'YOLO v8 detecta EPIs em menos de 100ms com 96% de precisão.',
  },
  {
    icon: AlertTriangle,
    title: 'Alertas Instantâneos',
    description: 'Notificação imediata quando EPI faltante é detectado.',
  },
  {
    icon: Camera,
    title: 'Evidência Automática',
    description: 'Registro fotográfico de cada desvio para auditoria.',
  },
  {
    icon: FileCheck,
    title: 'Relatórios de Compliance',
    description: 'Relatórios detalhados por NR, área e período.',
  },
  {
    icon: Shield,
    title: 'Compliance Total',
    description: 'Monitore conformidade com todas as NRs relevantes.',
  },
  {
    icon: Clock,
    title: 'Histórico Completo',
    description: 'Rastreie todos os desvios e ações corretivas.',
  },
]

const stats = [
  { value: '96%', label: 'Precisão Detecção' },
  { value: '<100ms', label: 'Tempo Resposta' },
  { value: '-67%', label: 'Acidentes' },
  { value: '35+', label: 'NRs Monitoradas' },
]

export default function SegurancaEPIPage() {
  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-br from-orange-500/20 to-transparent rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium mb-6">
              <HardHat className="w-4 h-4" />
              MÓDULO - SEGURANÇA & EPI
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Segurança que{' '}
              <span className="bg-gradient-to-r from-orange-400 to-amber-500 bg-clip-text text-transparent">
                você pode ver.
              </span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
              Detecção automática de EPIs por visão computacional. 
              Compliance com NRs em tempo real. Zero acidentes.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/demo"
                className="px-8 py-4 rounded-xl font-semibold text-white transition-all flex items-center gap-2"
                style={{ 
                  background: 'linear-gradient(135deg, #F97316 0%, #F59E0B 100%)',
                  boxShadow: '0 4px 14px 0 rgba(249, 115, 22, 0.39)'
                }}
              >
                Testar Grátis
                <ArrowRight className="w-5 h-5" />
              </Link>
              <button className="px-8 py-4 rounded-xl font-semibold text-white bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-all flex items-center gap-2">
                <Play className="w-5 h-5" />
                Ver Demo
              </button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 max-w-3xl mx-auto">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                <p className="text-3xl font-bold text-orange-400">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* EPIs Detection */}
      <section className="py-20 bg-[#0F172A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              EPIs Detectados Automaticamente
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Nossa IA identifica 8 tipos de EPIs em tempo real
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 max-w-4xl mx-auto">
            {epis.map((epi) => (
              <div
                key={epi.name}
                className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 text-center hover:border-orange-500/30 transition-all"
              >
                <div className="text-4xl mb-2">{epi.icon}</div>
                <p className="text-sm text-slate-300">{epi.name}</p>
                <CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto mt-2" />
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* NRs */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Compliance com NRs
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Monitore conformidade com mais de 35 Normas Regulamentadoras
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {nrs.map((nr) => (
              <div
                key={nr.code}
                className={`p-4 rounded-xl border ${
                  nr.essential 
                    ? 'bg-orange-500/10 border-orange-500/30' 
                    : 'bg-slate-800/50 border-slate-700'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-bold ${nr.essential ? 'text-orange-400' : 'text-slate-300'}`}>
                    {nr.code}
                  </span>
                  {nr.essential && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400">
                      Essencial
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-400">{nr.name}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-slate-500 mt-6">
            + 28 outras NRs monitoradas
          </p>
        </div>
      </section>
      
      {/* Features */}
      <section className="py-20 bg-[#0F172A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Recursos de Segurança
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Proteção completa para sua operação
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon
              return (
                <div
                  key={feature.title}
                  className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-orange-500/30 transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-orange-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400">{feature.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>
      
      {/* Alert Demo */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="p-6 rounded-2xl bg-rose-500/10 border border-rose-500/20">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-rose-500/20">
                  <AlertTriangle className="w-6 h-6 text-rose-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Alerta de EPI</h4>
                    <span className="text-xs text-slate-400">Agora</span>
                  </div>
                  <p className="text-slate-300 mb-3">
                    <strong>João Silva</strong> detectado sem capacete na <strong>Área de Produção</strong>
                  </p>
                  <div className="flex gap-2">
                    <button className="px-3 py-1.5 rounded-lg text-sm font-medium bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 transition-colors">
                      Notificar
                    </button>
                    <button className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">
                      Ver Evidência
                    </button>
                    <button className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">
                      Ignorar
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-center text-sm text-slate-500 mt-4">
              Exemplo de alerta em tempo real
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-orange-500/10 to-amber-600/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Proteja sua equipe com IA
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Comece gratuitamente por 14 dias. Sem cartão de crédito.
          </p>
          <Link
            to="/demo"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white transition-all"
            style={{ 
              background: 'linear-gradient(135deg, #F97316 0%, #F59E0B 100%)',
              boxShadow: '0 4px 14px 0 rgba(249, 115, 22, 0.39)'
            }}
          >
            Começar Teste Grátis
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}

